import React, { useContext, useEffect, useState } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CompanyReactContext } from '../../../../../contexts/CompanyContext';
import { CourseEditorContext } from '../../../../../contexts/CourseEditorContext';
import './ChapterEditor.scss';

export const LogoComponent = ({ 
    id,
    isSelected, 
    onSelect,
    borderRadius,
    backgroundColor,
    opacity = 1,
    mode = "edit",
    direction = "ltr",
    onAnimationComplete,
    isSequentialAnimationActive = false
}) => {
    const { i18n } = useContext(I18nReactContext);
    const { companyLogoUrl } = useContext(CompanyReactContext);
    const courseEditorContext = useContext(CourseEditorContext);
    const [isAIAnimating, setIsAIAnimating] = useState(false);

    // AI Logo Animation Effect
    useEffect(() => {
        const isAIGenerating = courseEditorContext?.isGeneratingChapter;
        const isShowingAIAnimations = courseEditorContext?.isShowingAIChapterAnimations;
        const hasLogo = !!companyLogoUrl && companyLogoUrl !== '';

        // Only animate if (AI is generating OR showing AI chapter animations OR sequential animations are active) and we have a logo and we're not in edit mode
        if ((isAIGenerating || isShowingAIAnimations || isSequentialAnimationActive) && hasLogo && mode !== "edit") {
            setIsAIAnimating(true);

            // For sequential animations during AI generation, trigger completion almost immediately
            // For other cases, use the full 2-second animation duration
            const completionDelay = isSequentialAnimationActive ? 200 : 2000;

            // Stop animation after the appropriate delay
            const animationTimeout = setTimeout(() => {
                setIsAIAnimating(false);
                // Call animation completion callback for sequential animations
                if (isSequentialAnimationActive && onAnimationComplete) {
                    onAnimationComplete(id);
                }
            }, completionDelay);

            return () => {
                clearTimeout(animationTimeout);
            };
        } else {
            setIsAIAnimating(false);
            // If sequential animations are active but we're not showing animation,
            // immediately call completion callback (even without logo to prevent sequence from getting stuck)
            if (isSequentialAnimationActive && onAnimationComplete) {
                setTimeout(() => onAnimationComplete(id), 100);
            }
        }
    }, [companyLogoUrl, courseEditorContext?.isGeneratingChapter, courseEditorContext?.isShowingAIChapterAnimations, mode, isSequentialAnimationActive, onAnimationComplete, id]);

    const handleClick = (e) => {
        e.stopPropagation();
        if (!isSelected) {
            onSelect(id);
        }
    };

    // Parse border radius values or use defaults
    const topLeft = borderRadius?.topLeft || 0;
    const topRight = borderRadius?.topRight || 0;
    const bottomLeft = borderRadius?.bottomLeft || 0;
    const bottomRight = borderRadius?.bottomRight || 0;

    // Create style object for the logo container
    const containerStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'relative',
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
    };

    // Create style for the background with opacity
    const backgroundStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: backgroundColor || 'transparent',
        opacity: opacity,
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
    };

    // Create style object for the logo image
    const logoStyle = {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        position: 'relative',
        zIndex: 1, // Ensure logo appears on top of background
        transform: direction === 'rtl' ? 'scaleX(-1)' : 'none' // Mirror the logo horizontally for RTL
    };

    // Check if logo exists
    const logoExists = !!companyLogoUrl && companyLogoUrl !== '';

    return (
        <div 
            className={`LogoComponent ${isSelected ? 'selected' : ''} ${!logoExists ? 'logo-missing' : ''}`}
            onClick={handleClick}
        >
            <div className={`logo-container ${isAIAnimating ? 'ai-image-creation' : ''}`} style={containerStyle}>
                {/* Background div with opacity */}
                <div className="logo-container-background" style={backgroundStyle}></div>

                {/* Logo image or missing notice */}
                {logoExists ? (
                    <img 
                        src={companyLogoUrl} 
                        alt={i18n.translate('weezbeeCourse.editor.logoComponent.altText', { defaultValue: 'Company Logo' })} 
                        style={logoStyle}
                    />
                ) : (
                    <div className="logo-missing-notice">
                        <span>{i18n.translate('weezbeeCourse.editor.logoComponent.missingLogo', { defaultValue: 'Company logo is missing' })}</span>
                    </div>
                )}
            </div>
        </div>
    );
};
